import React, { useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
	addItemToEvent,
	getEventDetails,
	replaceEventItem,
	addExerciseToWorkout
} from '../../../state/events'
import {update as updateResourceLibrary} from '../../ResourceLibrary/slice'
import {useEvent} from '../../../hooks/events'
import { useLibraryExercises } from '../../exerciseList/hooks'
import {getExerciseLibrary} from '../../../state/exerciseLibrary'
import { useExerciseLibrary } from '../../../hooks/exerciseLibrary'
import Form from 'react-bootstrap/Form'
import Select from "react-select"
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import { useInView } from 'react-intersection-observer'
import Modal from 'react-bootstrap/Modal'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import YoutubeModal from '../../YoutubeModal/YoutubeModal'
import {addExercise} from '../../exerciseList/exerciseListSlice'
import {useUser} from '../../users/hooks'
import {slidingQueryComparision} from '../../../util/util'
import {update as updateVideoModal} from '../../VideoModal/slice'
import MultiSelect from "react-multi-select-component";
import classes from './Exercises.module.css'
import { useDebounceCallback } from '@react-hook/debounce'

const mapFiltersToOptions = filters => (filters || []).map(f => ({
	label: f.name,
	value: f.id
}))

const mapFiltersToOptionsMulti = filters => (filters || []).map(f => ({
	label: f.label,
	value: f.value
}))

const Creator = ({ show, handleClose }) => {

	//here we grab the library state that is part of the event editor
	const { 
		tgtResourceId:id, 
		tgtResourceType:type, 
		replace,
		insertPosition,
		superset,
		heading
	} = useSelector( state => state.resourceLibrary )
	//get replace state. This holds the id and type of the exercise
	//getting replaced. The id will be falsey if not replacing
	const replacing = Boolean(replace.id)
	const event = useEvent(type,id)

	const eventUser = useUser(event?.client_id)
	const addingToSharedClient = (
		eventUser?.account_type === 'client' && 
		eventUser?.user_trainer_id === eventUser?.gym_id
	)

	const user_id_number = useSelector(state => state.users.current_user.user_id_number)
	const isTrainer = useSelector(state=>state.users.current_user.account_type === 'trainer')
	const isPartOfGym = useSelector(state=>state.users.current_user.gym_id != 0)
	const gymResourceSettings = useSelector(state=>state.settings.gymResourceSettings)

	//If the gym has enabled shared client resource restrictions
	//and workout is being built for a shared client then hide the
	//add to gym library check box (it defaults to on so this prevents
	//exercies from getting added to a trainer's custom library)
	const gymLibraryOnly = (
		isPartOfGym && 
		gymResourceSettings?.shared_client_restrictions === 1  &&
		addingToSharedClient
	)

	/* --- hooks --- */

	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()

	const {
		equipment: equipmentList,
		exerciseLevels,
		muscleGroups,
	} = useExerciseLibrary()

	/* --- local state --- */
	const [status, setStatus] = useState('idle')
	const [name, setName] = useState('')
	const [muscleGroup, setMuscleGroup] = useState(null)
	const [level, setLevel] = useState(null)
	const [equipment, setEquipment] = useState(null)
	const [preview, setPreview] = useState(null)
	const [demoLink, setDemoLink] = useState('')
	const [notes, setNotes] = useState('')
	const [addToGymLibrary,setAddToGymLibrary] = useState(true)
	
	/* --- handlers --- */

	/*
	 * Handles two cases: 
	 * 1) adding new exercise 
	 * 2) replacing existing exercise with another
	 */
	const handleInsert = (exercise) => {
		const exercise_type = (
			exercise.muscle_id === 9 ?
				'cardio' : 'exercise'
		)
		if (replacing) {
			statusDispatch(replaceEventItem({
				current_exercise_type: replace.type,
				current_item_id: replace.id,
				item_db_id: exercise.exercise_db_id,
				event_type: 'workout',
			}), "exercise", "replace")
				.then(() => {
					//clear out replace
					dispatch(updateResourceLibrary({ replace: {} }))
				})
		} else {
			statusDispatch(addExerciseToWorkout({
				exercise_name:exercise.exercise_db_name,
				name:exercise.exercise_db_name,
				exercise_db_id: exercise.exercise_db_id,
				routine_order:insertPosition,
				after_heading:Number(Boolean(heading)),
				superset,
				workout_id: id,
			}), "exercise", "add")
		}
	}

	const handleCreate = () => {
		setStatus('pending')
		const payload = {
			action: "createExercise",
			muscle_group_id:muscleGroup?.value,
			level_id: level?.value,
			equipment_ids: equipment?.map(e => e.value),
			link: demoLink,
			preview,
			notes,
			name
		}
		if (addToGymLibrary) {
			payload.add_to_gym_library = 1;
		}

		statusDispatch(addExercise(payload), "exercise", "create")
		.then(({payload}) =>{
			//refresh library
			dispatch(getExerciseLibrary())
			setStatus('idle')
			//insert into workout
			handleInsert(payload.data.exercise)
			//close modal
			handleClose()
			//clear out existing data
			setName('')
			setLevel(null)
			setEquipment(null)
			setMuscleGroup(null)
			setDemoLink('')
			setPreview('')
			setNotes('')
		})
	}

	return (
		<Modal show={show} onHide={handleClose}>
			<Modal.Header closeButton>
				<strong>Create New Exercise</strong>
			</Modal.Header>
			<Modal.Body>
				<label>Name*</label>
				<Form.Control
					onChange={e => { setName(e.target.value) }}
					placeholder="New exercise name"
					className="mb-2"
				/>
				<label>Muscle Group*</label>
				<Select
					className="mb-2"
					maxMenuHeight={200}
					onChange={selectedOption => {
						setMuscleGroup(selectedOption)
					}}
					options={mapFiltersToOptions(muscleGroups)}
					placeholder="Muscle group"
				/>
				<label>Difficulty Level*</label>
				<Select
					maxMenuHeight={200}
					onChange={selectedOption => {
						setLevel(selectedOption)
					}}
					options={mapFiltersToOptions(exerciseLevels)}
					placeholder="Difficulty level"
					className="mb-2"
				/>
				<label>Equipment</label>
				<Select
					maxMenuHeight={200}
					onChange={selectedOptions => {
						setEquipment(selectedOptions)
					}}
					options={mapFiltersToOptions(equipmentList)}
					placeholder="Equipment"
					className="mb-2"
					isMulti
				/>
				<Form.Label className="mb-2">
					Preview Image/GIF (optional)
					<OverlayTrigger overlay={
						<Tooltip>Recommended Size: 450x400 pixels</Tooltip>
					}>
						<i className="fa fa-info-circle" />
					</OverlayTrigger>
				</Form.Label>
				<Form.Control
					onChange={ e => { setPreview(e.target.files[0]) } }
					className="mb-2"
					type="file"
					accept="image/*"
				/>
				<Form.Control
					onChange={e => { setDemoLink(e.target.value) }}
					placeholder="Optional Demo Link (eg https://youtu.be/gD1ahDaqQTo)"
					className="mb-2"
				/>
				<Form.Control
					onChange={e => { setNotes(e.target.value) }}
					placeholder="Optional notes that appear in the exercise notes section when exercises are added to workouts (eg Keep head up, Retract scapula.)"
					className="mb-2"
					as="textarea"
				/>
				{(isTrainer&&isPartOfGym&&!gymLibraryOnly) && <div className="ml-2">
					<Form.Check
						className="my-2"
						name="add to gym library"
						label={'Add to Gym Library'}
						checked={addToGymLibrary}
						onChange={(e) => {
							setAddToGymLibrary(e.target.checked);
						}}
					/>
				</div>}
				<Button
					disabled={
						status === 'pending' ||
						!name ||
						!level ||
						!muscleGroup
					}
					onClick={handleCreate}
					variant="success"
					block
				>
					{
						status === 'pending' ?
							<Spinner size="sm" animation="border" /> :
							'Create & Add Exercise'
					}
				</Button>
			</Modal.Body>
		</Modal>
	)
}

const Exercises = () => {
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch();
	//here we grab the library state that is part of the event editor
	const { tgtResourceId:id, tgtResourceType:type, insertPosition,superset,heading } = useSelector(state => state.resourceLibrary)

	const event = useEvent(type,id)
	//this only returns status and list of exercises
	const {
		exerciseLevels,
		muscleGroups,
		equipment,
	} = useExerciseLibrary()


	//get replace state. This holds the id and type of the exercise
	//getting replaced. The id will be falsey if not replacing
	const replace = useSelector(state => state.resourceLibrary.replace)
	const replacing = Boolean(replace.id)

	/* --- local state --- */
	const [query, setQuery] = useState('')

	const [muscleGroupFilters, setMuscleGroupFilters] = useState([])
	const [muscleGroupFiltersDisplay, setMuscleGroupFiltersDisplay] =useState([])
	
	const [exerciselevelFilters, setExerciselevelFilters] = useState([])
	const [exerciseLevelFiltersDisplay, setExerciseLevelFiltersDisplay] =useState([])

	const [equipmentFilters, setEquipmentFilters] = useState([])
	const [equipmentFiltersDisplay, setEquipmentFiltersDisplay] =useState([])
	
	const [demoVideoShow, setDemoVideoShow] = useState(false);
	const [demoVideo, setDemoVideo] = useState("");
	const [showCreator, setShowCreator] = useState(false)

	/**
	 * Handles two cases: 
	 * 1) adding new exercise 
	 * 2) replacing existing exercise with another
	 */
	const handleInsert = (exercise) => {
		const exercise_type = (
			//this means the exercise is cardio
			Number(exercise.muscleGroupData.id) === 9 ?
				'cardio' : 'exercise'
		)
		if (replacing) {
			statusDispatch(replaceEventItem({
				current_exercise_type: replace.type,
				current_item_id: replace.id,
				item_db_id: exercise.exercise_db_id,
				event_type: 'workout',
			}), "exercise", "replace")
				.then(() => {
					//clear out replace
					dispatch(updateResourceLibrary({ replace: {} }))
				})
		} else {
			statusDispatch(addExerciseToWorkout({
				name:exercise.exercise_db_name,
				exercise_db_id: exercise.exercise_db_id,
				routine_order:insertPosition,
				after_heading:Number(Boolean(heading)),
				workout_id: id,
				superset,
			}), exercise_type, "add")
			dispatch(updateResourceLibrary({ 
				insertPosition:insertPosition+1 
			}))
		}
	}
	const passesFitlers = (exercise) => {

		const groupMatch = (
			muscleGroupFilters.length > 0 ?
				muscleGroupFilters.includes(exercise.muscleGroupData.muscle_group) :
				true
		)

		const levelMatch = (
			exerciselevelFilters.length > 0 ?
				exerciselevelFilters.includes(exercise.levelData.name) :
				true
		)

		const equipmentMatch = equipmentFilters.reduce((carry, equipmentId) => (
			carry && exercise.equipmentData.map(e => Number(e.id)).includes(Number(equipmentId))
		), true)

		return groupMatch && levelMatch && equipmentMatch
	}

	const handleSetFilters = (e, type) => {
		let valueMap = []
		if(type === 'muscleGroup'){
			e.forEach(filter => {
				valueMap.push(filter.label)
			})
			setMuscleGroupFilters(valueMap)
			setMuscleGroupFiltersDisplay(e)
		} else if(type==='equipment'){
			e.forEach(filter => {
				valueMap.push(filter.value)
			})
			setEquipmentFilters(valueMap)
			setEquipmentFiltersDisplay(e)
		} else {
			e.forEach(filter => {
				valueMap.push(filter.label)
			})
			setExerciselevelFilters(valueMap)
			setExerciseLevelFiltersDisplay(e)
		}

	}

	//conctatenates all relevant exercise strings into single searchable
	//string to use with the sliding query comparison function
	const exerciseSearchString = (e)=>{
		return [
			String(e.exercise_db_name).toLowerCase(),
			String(e.notes).toLowerCase(),
			String(e.muscleGroupData.muscle_group).toLowerCase(),
			e.equipmentData.map(e=>e.name).join('----').toLowerCase()
		].filter(s=>!!s).join('----')
	}

	const handleQueryChange = q => {
		setQuery(q)
	}

	const debouncedHandleQueryChange = useDebounceCallback(
		handleQueryChange,300
	)

	const {status,exercises} = useLibraryExercises(event?.client_id)

	const exerciseBlockSize = 60
	const [displayCount, setDisplayCount] = useState(exerciseBlockSize)
	const filteredExercises = useMemo(
		() => exercises.filter(passesFitlers).sort((a, b) => {
			const aName = (a.name || "").toLowerCase()
			const bName = (b.name || "").toLowerCase()
			//when a search query is present the sorting is based on query
			//relevance smaller relevance values are better. In the sense
			//that they differ less from the intent of the search
			if (Boolean(query)) {
				const delta = slidingQueryComparision(
					exerciseSearchString(a),
					exerciseSearchString(b),
					query.toLowerCase()
				)
				return delta
			} else {

				const aMuscleGroup = (a.muscleGroupData.muscle_group || "").toLowerCase()
				const bMuscleGroup = (b.muscleGroupData.muscle_group || "").toLowerCase()

				const sortValue = (aMuscleGroup === bMuscleGroup ?
					aName < bName ? -1 : 1 :
					aMuscleGroup < bMuscleGroup ? -1 : 1
				)
				return sortValue
			}

		}),
		[query, muscleGroupFilters, exerciselevelFilters, equipmentFilters]
	);
	const filteredExercisesSlice = useMemo(
		() => filteredExercises.slice(0, displayCount),
		[filteredExercises, displayCount]
	)

	const { ref: scrollRef, inView } = useInView({
		/* Optional options */
		threshold: 0,
	});

	useEffect(() => {
		if (inView && displayCount < filteredExercises.length) {
			setDisplayCount(displayCount + exerciseBlockSize)
		}
	}, [inView])

	//reset the display count everytime the fitlers or search query or list change
	useEffect(() => {
		setDisplayCount(exerciseBlockSize)
	}, [query, muscleGroupFilters, exerciselevelFilters, equipmentFilters])

	return (
		<>
			<div
				style={{
					width: '100%',
					height: '100%',
				}}
				className="d-flex align-items-stretch flex-column"
			>
				<div style={{
					background: '#e5e7f5'
				}}>
					<div className="px-1">
						<strong>Exercises</strong>
						<Form.Control
							onChange={ e => {
								debouncedHandleQueryChange(e.target.value)
							}}
							placeholder="search"
						/>
					</div>
					<div className="d-flex px-1">
						<div style={{ flex: '1 1 33%' }}>
							<small>Muscle Group:</small>
							<MultiSelect
								className={classes.muscleGroupFilter}
								onChange={e =>	handleSetFilters(e, 'muscleGroup')}
								value={muscleGroupFiltersDisplay}
								options={mapFiltersToOptions(muscleGroups) ? mapFiltersToOptions(muscleGroups) : []}
								labelledBy="Select"
							/>
						</div>
						<div style={{ flex: '1 1 33%' }}>
							<small>Level:</small>
							<MultiSelect
								className={classes.muscleGroupFilter}
								onChange={e =>	handleSetFilters(e, 'exerciseLevel')}
								value={exerciseLevelFiltersDisplay}
								options={mapFiltersToOptions(exerciseLevels) ? mapFiltersToOptions(exerciseLevels) : []}
								labelledBy="Select"
							/>
						</div>
						<div style={{ flex: '1 1 33%' }}>
							<small>Equipment:</small>
							<MultiSelect
								className={classes.muscleGroupFilter}
								onChange={e =>	handleSetFilters(e, 'equipment')}
								value={equipmentFiltersDisplay}
								options={mapFiltersToOptions(equipment) ? mapFiltersToOptions(equipment) : []}
								labelledBy="Select"
							/>
						</div>
					</div>
					<Button onClick={() => { setShowCreator(true) }} size="sm" variant="link" style={{color:"#28a745"}}>
						<i className="fas fa-plus" /> Create new exercise
					</Button>
				</div>
				<div style={{
					overflow: 'auto',
					flex: '1 1 auto'
				}}>
					{filteredExercisesSlice.map( exercise => (
						<div
							key={exercise.exercise_db_id}
							className="d-flex align-items-center p-1"
							style={{ borderBottom: '1px solid #ccc' }}
						>
							<OverlayTrigger overlay={
								<Tooltip>{replacing ? 'Replace Selected Exercise' : 'Add to Routine'}</Tooltip>
							}>
								<Button
									className="mr-2"
									variant="outline-success"
									size="sm"
									onClick={() => {
										handleInsert(exercise)
									}}
								>
									<i className={replacing ? "fas fa-exchange-alt" : "fas fa-plus"} />
								</Button>
							</OverlayTrigger>
							<div style={{flex:'0 0 auto'}}>
								<div style={{ font: '14px' }}>{exercise.name}</div>
								<div className="d-flex align-items-center text-muted">
									{Boolean(exercise.levelData.name)&&
										<small className="text-muted">{exercise.levelData.name}</small>
									}
									{(Boolean(exercise.levelData.name)&&Boolean(exercise.muscleGroupData.name))&&
										<small className="mx-1">|</small>
									}
									{Boolean(exercise.muscleGroupData.name)&&
										<small className="text-muted">{exercise.muscleGroupData.name}</small>
									}
									{(Boolean(exercise.muscleGroupData.name)&&exercise.equipmentData.length>0)&&
										<small className="mx-1">|</small>
									}
									{exercise.equipmentData.length>0&&
										<small className="mx-1">
											{exercise.equipmentData.map(e=>e.name).join(', ')}
										</small>
									}
								</div>
							</div>
							<div className="ml-auto">
								{Boolean(exercise.previewData.src) &&
									<OverlayTrigger
										placement="left"
										overlay={
											<Tooltip id={`tooltip-left`} >
												{exercise.name}
												<img
													src={`${exercise.previewData.src}`}
													style={{ width: `${exercise.previewData?.dims?.width || 180}px` }}
												/>
											</Tooltip>
										}
									>
										<Button variant="link">
											<i className="fas fa-eye" />
										</Button>
									</OverlayTrigger>
								}
								{Boolean(exercise.demoLink) &&
									<OverlayTrigger
										key={`${exercise.demoLink}`}
										placement={"left"}
										overlay={
											<Tooltip
												id={`tooltip-left`}
											><span>Video demo</span>
											</Tooltip>
										}
									>
										<i
											className={"fas fa-video"}
											style={{
												marginRight: "10px",
												cursor: "pointer",
											}}
											onClick={(e) => {
												setDemoVideo(exercise.demoLink);
												setDemoVideoShow(true);
												e.stopPropagation();
											}}
										></i>
									</OverlayTrigger>
								}
								{Boolean(exercise.demo_video) && (
									<OverlayTrigger
										key={`${exercise.demo_video}`}
										placement={"top"}
										overlay={
											<Tooltip
												id={`tooltip-top`}
												className={classes.Tooltip}
											>
												Demo Video
											</Tooltip>
										}
									>
										<Button
											size="sm"
											className="text-dark mr-2 fas fa-video"
											variant="link"
											onClick={(e) => {
												dispatch(updateVideoModal({
													show:true,
													src:exercise.demo_video,
													heading:exercise.exercise_db_name
												}))
											}}
										/>
									</OverlayTrigger>
								)}
							</div>
						</div>
					))}
					{displayCount < filteredExercises.length &&
						<div ref={scrollRef}>
							Loading more...
						</div>
					}
				</div>
			</div>
			<Creator show={showCreator} handleClose={() => { setShowCreator(false) }} />
			<YoutubeModal setDemoVideoShow={setDemoVideoShow} demoVideoShow={demoVideoShow} demoVideo={demoVideo}/>
		</>
	)
}

export default Exercises